import React, { useState, useEffect, useMemo } from "react";
import { Row, Col } from "antd";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

// Redux
import { connect } from "react-redux";
import {
  getLongDay,
  getTimeZone,
  getAfterMorningPrimeNoon,
  getUserInfo,
} from "redux/selectors";

// Common
import COLORS from "common/Colors";
import { DEVICE } from "common/Device";
import { MAIN_CLIENT_ID, getTimeZoneByCountry } from "common/Constants";

// Components
import LeaveForm from "organisms/LeaveForm";

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const SkeletonBlock = styled.div`
  width: ${(p) => p.width || "100px"};
  height: ${(p) => p.height || "16px"};
  margin-bottom: ${(p) => p.mb || "0"};
  border-radius: 4px;
  background-color: #e0e0e0;
  background-image: linear-gradient(90deg, #e0e0e0, #f5f5f5, #e0e0e0);
  background-size: 200px 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

const StyledRow = styled(Row)`
  margin-top: -0.9rem;
  margin-left: 2rem;
  padding-top: 5px;
  .align-left {
    display: flex;
    flex-direction: column;
    @media ${DEVICE.tablet} {
      margin-top: 3rem;
    }
    @media (max-width: 426px) {
      margin-top: 5rem;
    }
  }
`;

const StyledTime = styled.span`
  font-size: 1.2rem;
  color: ${COLORS.TEXT.SECONDARY};
  font-weight: bold;
`;

const StyledAMPM = styled.span`
  color: ${COLORS.TEXT.SECONDARY};
  font-size: 12px;
  padding-left: 10px;
  font-weight: 600;
  white-space: nowrap;
  @media ${DEVICE.tablet} {
    font-size: 10px;
    margin-left: -10px;
  }
  @media ${DEVICE.mobileL} {
    font-size: 10px;
  }
`;

const StyledDate = styled.div`
  color: ${COLORS.TEXT.SECONDARY};
  font-size: 12px;
  margin-top: -45px;
  white-space: nowrap;
  @media ${DEVICE.tablet} {
    font-size: 8px;
    margin-left: -10px;
  }
`;

const FALLBACK_TIMEZONE = "Africa/Nairobi";
const SKELETON_MAX_MS = 2000; // never show the loader longer than this

// Normalise whatever shape the API/store returns (object, array, or { data })
const normalizeUser = (user) => {
  if (!user) return {};
  if (Array.isArray(user)) return user[0] || {};
  if (user.data && typeof user.data === "object" && !Array.isArray(user.data)) {
    return { ...user, ...user.data };
  }
  if (Array.isArray(user.data)) return { ...user, ...(user.data[0] || {}) };
  return user;
};

// Country comes from the user's managing office, e.g. "BDO EA Uganda".
// getTimeZoneByCountry matches the country name inside that string.
const getCountryFromUser = (user) => {
  const managing = user?.managingoffice ?? user?.managingOffice;
  return (
    user?.country ??
    user?.Country ??
    (Array.isArray(managing) ? managing[0] : managing) ??
    user?.countryName ??
    ""
  );
};

const getClientIdFromUser = (user) =>
  user?.clientid ?? user?.clientId ?? user?.ClientId ?? user?.ClientID ?? "";

// Returns the zone if Intl accepts it, otherwise the fallback (never throws)
const resolveTimeZone = (zone) => {
  if (typeof zone !== "string" || !zone.trim()) return FALLBACK_TIMEZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone.trim() });
    return zone.trim();
  } catch (e) {
    return FALLBACK_TIMEZONE;
  }
};

const safeGetTimeZoneByCountry = (country) => {
  try {
    return getTimeZoneByCountry(country);
  } catch (e) {
    return FALLBACK_TIMEZONE;
  }
};

// Live clock for a given IANA timezone (always runs, falls back to Nairobi)
const useZonedClock = (rawTimeZone) => {
  const timeZone = resolveTimeZone(rawTimeZone);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [timeZone]);

  return useMemo(() => {
    const timeParts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(now);

    const get = (type) => timeParts.find((p) => p.type === type)?.value || "";

    const longDay = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(now);

    return {
      time: `${get("hour")}:${get("minute")}`,
      ampm: get("dayPeriod"),
      longDay,
    };
  }, [now, timeZone]);
};

function DashboardTop({
  longDay: storeLongDay,
  timeZone: storeTime,
  amPM: storeAmPM,
  userInfo: rawUserInfo,
}) {
  const [displayLeaveModal, toggleLeaveModal] = useState(false);
  const [graceExpired, setGraceExpired] = useState(false);

  const userInfo = normalizeUser(rawUserInfo);
  const country = getCountryFromUser(userInfo);
  const clientId = getClientIdFromUser(userInfo);

  const isUserLoaded = Boolean(
    userInfo?.employeeid || userInfo?.employeeId || clientId || country
  );

  // Safety net: stop showing the skeleton after a short grace period,
  // even if user info never arrives (e.g. server returns a different shape)
  useEffect(() => {
    if (isUserLoaded) return undefined;
    const id = setTimeout(() => setGraceExpired(true), SKELETON_MAX_MS);
    return () => clearTimeout(id);
  }, [isUserLoaded]);

  const isMainClient = isUserLoaded && clientId === MAIN_CLIENT_ID;
  const zone = safeGetTimeZoneByCountry(country);

  // Clock always runs, so there is always something to show
  const clock = useZonedClock(zone);

  // Use store values only for main clients AND only if they actually have data
  const useStore = isMainClient && Boolean(storeTime);

  const displayTime = useStore ? storeTime : clock.time;
  const displayAmPM = useStore ? storeAmPM : clock.ampm;
  const displayDay = useStore && storeLongDay ? storeLongDay : clock.longDay;

  const showSkeleton = !isUserLoaded && !graceExpired;

  return (
    <>
      <StyledRow>
        <Col xs={24} md={4} sm={24} className="align-left">
          {showSkeleton ? (
            <div>
              <SkeletonBlock width="80px" height="20px" mb="8px" />
              <SkeletonBlock width="130px" height="12px" />
            </div>
          ) : (
            <>
              <div>
                <StyledTime>{displayTime}</StyledTime>
                <StyledAMPM>{displayAmPM}</StyledAMPM>
              </div>
              <StyledDate>{displayDay}</StyledDate>
            </>
          )}
        </Col>
      </StyledRow>
      <LeaveForm
        isModalVisible={displayLeaveModal}
        closeModal={() => toggleLeaveModal(false)}
        leaveDetails={{}}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  longDay: getLongDay(state),
  timeZone: getTimeZone(state),
  amPM: getAfterMorningPrimeNoon(state),
  userInfo: getUserInfo(state),
});

export default connect(mapStateToProps, null)(DashboardTop);